import React, { useEffect, useRef, useState } from "react";
import Banner from "../Sections/Home/Banner/Banner";
import Seo from "../Sections/Home/SEO/Seo";
import Challenge from "../Sections/Home/Challenge/Challenge";
import PhysicsBalls from "../Sections/Home/Challenge/PhysicsBalls";
import Customer from "../Sections/Home/Customer/Customer";
import Driver from "../Sections/Home/Driver/Driver";
import Company from "../Sections/Home/Company/Company";
import Choose from "../Sections/Home/Driver/Choose";
import SignUp from "../Sections/Home/SignUp/SignUp";
import FAQ from "../Sections/Home/FAQs/FAQ";
import FreeApp from "../Sections/Download/FreeApp";
import Card from "../Sections/Home/Card/Card";
import Header from "../Sections/Home/Header/Header";
import Footer from "../Footer/Footer";

const initialScale = 1;
const minScale = 0.2;
const minHeight = 768;

const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [bannerHeightState, setBannerHeightState] = useState(1440);

  const bannerRef = useRef(null);
  const customerRef = useRef(null);
  const customerContainerRef = useRef(null);
  const driverRef = useRef(null);
  const driverContainerRef = useRef(null);
  const companyRef = useRef(null);
  const companyContainerRef = useRef(null);

  const [customerInView, setCustomerInView] = useState(false);
  const [customerPassed, setCustomerPassed] = useState(false);
  const [driverInView, setDriverInView] = useState(false);
  const [driverPassed, setDriverPassed] = useState(false);
  const [companyInView, setCompanyInView] = useState(false);
  const [companyPassed, setCompanyPassed] = useState(false);

  // Responsive max scroll limits for smooth scaling
  const getResponsiveMaxScroll = () => {
    if (window.innerWidth > 1440) return 2200;
    if (window.innerWidth > 1024) return 2000;
    return 1800;
  };
  const maxScroll = getResponsiveMaxScroll();

  // Measure Banner height on mount & resize
  useEffect(() => {
    const updateBannerHeight = () => {
      if (bannerRef.current) {
        setBannerHeightState(bannerRef.current.offsetHeight);
      }
    };
    updateBannerHeight();
    window.addEventListener("resize", updateBannerHeight);
    return () => window.removeEventListener("resize", updateBannerHeight);
  }, []);

  // Scroll event with requestAnimationFrame throttling
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Window resize handler
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Intersection Observers for customer, driver, company sections
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === customerRef.current) setCustomerInView(entry.isIntersecting);
          if (entry.target === customerContainerRef.current) setCustomerPassed(!entry.isIntersecting);

          if (entry.target === driverRef.current) setDriverInView(entry.isIntersecting);
          if (entry.target === driverContainerRef.current) setDriverPassed(!entry.isIntersecting);

          if (entry.target === companyRef.current) setCompanyInView(entry.isIntersecting);
          if (entry.target === companyContainerRef.current) setCompanyPassed(!entry.isIntersecting);
        });
      },
      { threshold: 0.1 }
    );

    if (customerRef.current) observer.observe(customerRef.current);
    if (customerContainerRef.current) observer.observe(customerContainerRef.current);
    if (driverRef.current) observer.observe(driverRef.current);
    if (driverContainerRef.current) observer.observe(driverContainerRef.current);
    if (companyRef.current) observer.observe(companyRef.current);
    if (companyContainerRef.current) observer.observe(companyContainerRef.current);

    return () => {
      if (customerRef.current) observer.unobserve(customerRef.current);
      if (customerContainerRef.current) observer.unobserve(customerContainerRef.current);
      if (driverRef.current) observer.unobserve(driverRef.current);
      if (driverContainerRef.current) observer.unobserve(driverContainerRef.current);
      if (companyRef.current) observer.unobserve(companyRef.current);
      if (companyContainerRef.current) observer.unobserve(companyContainerRef.current);
    };
  }, []);

  // Calculate scroll factor and easing for smooth animation
  const scrollFactorRaw = Math.min(scrollY / maxScroll, 1);
  const scrollFactor = Math.pow(scrollFactorRaw, 2); // ease-in quadratic for smoothness

  // Calculate dynamic scale and banner height based on scroll
  const scale = initialScale - (initialScale - minScale) * scrollFactor;
  const bannerHeight = bannerHeightState * (1 - scrollFactor) + minHeight * scrollFactor;
  const borderRadius = scale > minScale ? 100 * scrollFactor : 0;

  const borderRadiusCustomer = customerInView ? 0 : scale > minScale ? 32 * scrollFactor : 0;
  const borderRadiusDriver = driverInView ? 0 : scale > minScale ? 32 * scrollFactor : 0;
  const borderRadiusCompany = companyInView ? 0 : scale > minScale ? 32 * scrollFactor : 0;

  const challengeTranslateY = -300 * scrollFactor;

  return (
    <>
      <Header borderRadius={borderRadius} />

      {/* Placeholder div to preserve layout spacing during banner scale */}
        <div
          style={{
            height:window.innerWidth>1024?'2440px':window.innerWidth>768?'2000px':'auto',
            transition: "height 0.3s ease",
          }}
        />
      {/* Banner Section */}
      {windowWidth > 768? (
        <div
          ref={bannerRef}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            overflow: "hidden",
            height: 'auto',
            transform: `scale(${scale}) translateY(${scrollFactor>0.98?'-1200px':'0px'})`,
            transformOrigin: "top right",
            transition: "border-radius 0.1s ease, transform 0.3s ease, height 0.3s ease",
            willChange: "transform, border-radius",
            borderRadius: `${borderRadius}px`,
            background: "white",
          }}
        >
          <Banner />
        </div>
      ) : (
        <Banner />
      )}

      {/* SEO Section */}
      <div
        className="challenge-component"
      >
        <Seo />
      </div>

      <Card />
      <Challenge />
      <PhysicsBalls />

      {/* Customer Section */}
      {/* {windowWidth > 1111 ? (
        <div ref={customerContainerRef}>
          <div
            ref={customerRef}
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              overflow: "hidden",
              transform: customerInView
                ? "translate(0, 0) scale(1)"
                : "translate(-100px, -50px) scale(0.1)",
              opacity: customerInView ? 1 : customerPassed ? 0 : 1,
              transformOrigin: "top left",
              transition: "transform 0.6s ease-out, opacity 0.3s ease",
              willChange: "transform, opacity",
              borderRadius: `${borderRadiusCustomer}px`,
            }}
          >
            <Customer />
          </div>
        </div>
      ) : (
        <Customer />
      )} */}

      {/* Driver Section */}
      {/* {windowWidth > 1111 ? (
        <div ref={driverContainerRef}>
          <div
            ref={driverRef}
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              overflow: "hidden",
              transform: driverInView
                ? "translate(0, 0) scale(1)"
                : "translate(-100px, -50px) scale(0.1)",
              opacity: driverInView ? 1 : driverPassed ? 0 : 1,
              transformOrigin: "top right",
              transition: "transform 0.6s ease-out, opacity 0.3s ease",
              willChange: "transform, opacity",
              borderRadius: `${borderRadiusDriver}px`,
            }}
          >
            <Driver />
          </div>
        </div>
      ) : (
        <Driver />
      )} */}

      {/* Company Section */}
      {/* {windowWidth > 1111 ? (
        <div ref={companyContainerRef}>
          <div
            ref={companyRef}
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              overflow: "hidden",
              transform: companyInView
                ? "translate(0, 0) scale(1)"
                : "translate(-100px, -50px) scale(0.1)",
              opacity: companyInView ? 1 : companyPassed ? 0 : 1,
              transformOrigin: "top left",
              transition: "transform 0.6s ease-out, opacity 0.3s ease",
              willChange: "transform, opacity",
              borderRadius: `${borderRadiusCompany}px`,
            }}
          >
            <Company />
          </div>
        </div>
      ) : (
        <Company />
      )} */}
    <Customer></Customer>
    <Driver></Driver>
    <Company></Company>
      <Choose />
      <SignUp />
      <FAQ />
      <FreeApp />
      <Footer />
    </>
  );
};

export default Home;
